import React, { useState, useEffect, useRef } from 'react';
import {
  DatePicker, TimePicker, Select, Button,
  message, Modal, Space, Divider, Badge, Spin, Alert, Tooltip,
} from 'antd';
import moment from 'moment';
import {
  collection, query, where, getDocs,
  addDoc, doc, getDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { Option } = Select;

const seatList = [
  [25,520,175],[26,520,110],[27,520,45],[28,485,115],[29,485,180],[30,485,245],
  [31,385,245],[32,385,180],[33,385,115],[34,305,80],[35,240,80],[36,175,80],
  [37,110,80],[38,48,80],[39,44,160],[40,110,160],[41,175,160],[42,240,160],
  [43,241,260],[44,175,260],[45,110,260],[46,44,260],[47,245,580],[48,180,580],
  [50,247,690],[49,180,690],[51,110,680],[52,70,635],[53,110,590],[20,1125,590],[21,1060,590],[22,1000,590],[23,935,590],[24,870,590],
  [19,1120,520],[18,1060,520],[17,990,520],[14,1120,410],[15,1055,410],[16,990,410],
  [13,1128,360],[12,1065,360],[11,997,360],[10,935,360],[1,840,170],[2,840,105],
  [3,840,40],[4,965,40],[6,965,175],[5,965,110],[7,985,260],[8,920,260],[9,855,260],
];

export default function Booking() {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const userId = currentUser?.uid;
  const navigate = useNavigate();

  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [duration, setDuration] = useState(5);
  const [slots, setSlots] = useState(null);
  const [deskStatus, setDeskStatus] = useState({});
  const [selectedDeskId, setSelectedDeskId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [options, setOptions] = useState([]);
  const [openingHours, setOpeningHours] = useState({ startHour: 9, endHour: 20 });
  const [beforeMinutes, setBeforeMinutes] = useState(120); // default 120 min ahead
  const [fullName, setFullName] = useState('Utilisateur');
  const [cancelledUntil, setCancelledUntil] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('ws-theme') === 'dark');
  const [isMobile, setIsMobile] = useState(false);
  const imgRef = useRef(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

  // Native dimensions of PlanSvg.svg — adjust if your SVG viewBox differs
  const NATIVE_W = 1200;
  const NATIVE_H = 750;

  const updateImgSize = () => {
    if (imgRef.current) {
      setImgSize({ w: imgRef.current.offsetWidth, h: imgRef.current.offsetHeight });
    }
  };

  useEffect(() => {
    const check = () => { setIsMobile(window.innerWidth < 640); updateImgSize(); };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const toggleTheme = () => {
    setDark(d => {
      localStorage.setItem('ws-theme', !d ? 'dark' : 'light');
      return !d;
    });
  };

  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    if (!userId) { message.error('Veuillez vous connecter.'); navigate('/login'); }
  }, [userId, navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'administration', 'Centre'));
        const data = snap.data();
        const s = data?.reservationsStart?.toDate();
        const e = data?.reservationsEnd?.toDate();
        if (s && e) setOpeningHours({ startHour: s.getHours(), endHour: e.getHours() });
        if (data?.before != null) setBeforeMinutes(Number(data.before));
      } catch {}
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'administration', 'Abonnement'));
        const durations = snap.data()?.['hours membership'];
        if (Array.isArray(durations) && durations.length) {
          setOptions(durations);
          if (!durations.includes(duration)) setDuration(durations[0]);
        }
      } catch { message.error('Erreur récupération des durées.'); }
    };
    load();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      if (!userId) return;
      try {
        const q = query(collection(db, 'contact'), where('FirebaseId', '==', userId));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0].data();
          setFullName(d.fullname || d.name || 'Utilisateur');

          // Check cooldown from a previous cancellation
          if (d.cancelledUntil) {
            const until = d.cancelledUntil.toDate ? d.cancelledUntil.toDate() : new Date(d.cancelledUntil);
            setCancelledUntil(until);
            if (until > new Date()) {
              const formatted = dayjs(until).format('HH:mm [le] DD/MM/YYYY');
              message.warning({
                content: `Suite à une annulation, vous pouvez réserver à partir de ${formatted}.`,
                duration: 8,
              });
            }
          }
        }
      } catch {}
    };
    fetch();
  }, [userId]);

  useEffect(() => {
    if (!slots) return;
    const fetchStatus = async () => {
      setCheckingAvailability(true);
      const { start, end } = slots;
      const allStatuses = {};
      const cols = ['visiteur', 'reservation'];
      await Promise.all(cols.map(async col => {
        const q = query(collection(db, col), where('startTime', '<', end.toDate()));
        const snap = await getDocs(q);
        snap.docs.forEach(doc => {
          const data = doc.data();
          const deskId = data.table;
          if (!deskId) return;
          if (col === 'visiteur' && data.sortie !== true) {
            const st = data.startTime.toDate();
            const ve = new Date(st.getTime() + (data.hours || 0) * 3600000);
            if (ve > start.toDate()) allStatuses[deskId] = 'red';
          } else if (col === 'reservation') {
            const rs = data.startTime?.toDate();
            const re = data.endTime?.toDate();
            if (rs && re && rs < end.toDate() && re > start.toDate())
              allStatuses[deskId] = 'yellow';
          }
        });
      }));
      for (let id = 1; id <= 56; id++) {
        if (!allStatuses[id]) allStatuses[id] = 'green';
      }
      setDeskStatus(allStatuses);
      setCheckingAvailability(false);
    };
    fetchStatus();
  }, [slots]);

  const loadSlots = () => {
    if (!date || !time) return message.warning("Veuillez sélectionner la date et l'heure.");
    const start = date.clone().hour(time.hour()).minute(time.minute()).second(0);
    const end = start.clone().add(duration, 'hours');
    // Enforce the "before" constraint
    const earliest = dayjs().add(beforeMinutes, 'minute');
    if (dayjs(start.toDate()).isBefore(earliest)) {
      return message.warning(
        `La réservation doit être faite au moins ${beforeMinutes} min à l'avance. Première heure disponible : ${earliest.format('HH:mm')}.`
      );
    }
    setSlots({ start, end });
  };

  const onDeskClick = id => {
    const color = deskStatus[id];
    if (!slots?.start) return message.warning("Cliquez sur 'Vérifier' d'abord.");
    if (color !== 'green') return message.info(`Table ${id} est ${color === 'yellow' ? 'déjà réservée' : 'actuellement occupée'}.`);
    setSelectedDeskId(id);
    setIsModalVisible(true);
  };

  const handleConfirm = async () => {
    if (!userId || !selectedDeskId || !slots?.start) return;

    // Check cancellation cooldown
    if (cancelledUntil && cancelledUntil > new Date()) {
      const formatted = dayjs(cancelledUntil).format('HH:mm');
      message.error(`Vous ne pouvez pas réserver avant ${formatted} suite à une annulation.`);
      setIsModalVisible(false);
      return;
    }

    setLoading(true);
    try {
      const dayStart = slots.start.clone().startOf('day').toDate();
      const dayEnd = slots.start.clone().endOf('day').toDate();
      const q = query(collection(db, 'reservation'), where('userId', '==', userId), where('startTime', '>=', dayStart), where('startTime', '<=', dayEnd));
      const snap = await getDocs(q);
      if (snap.docs.length > 0) { setLoading(false); return message.error('Vous avez déjà réservé ce jour.'); }
      await addDoc(collection(db, 'reservation'), {
        userId, fullname: fullName, table: selectedDeskId,
        startTime: slots.start.toDate(), endTime: slots.end.toDate(), cancelled: false,
      });

      // Mirror to reservation_history with extra metadata
      const reservationId = `${userId}_${selectedDeskId}_${slots.start.valueOf()}`;
      await addDoc(collection(db, 'reservation_history'), {
        userId,
        fullname: fullName,
        table: selectedDeskId,
        startTime: slots.start.toDate(),
        endTime: slots.end.toDate(),
        duration,
        cancelled: false,
        status: 'confirmed',
        source: 'mobile',
        room: 'WeStudy',
        reservationId,
        createdAt: new Date(),
      });
      message.success(`Table ${selectedDeskId} réservée avec succès !`);
      setIsModalVisible(false);
      setSelectedDeskId(null);
      setSlots({ ...slots });
    } catch { message.error('Erreur lors de la réservation.'); }
    finally { setLoading(false); }
  };

  const availableCount = Object.values(deskStatus).filter(s => s === 'green').length;
  const reservedCount  = Object.values(deskStatus).filter(s => s === 'yellow').length;
  const occupiedCount  = Object.values(deskStatus).filter(s => s === 'red').length;

  const colorMap = { green: '#16a34a', yellow: '#d97706', red: '#dc2626', lightgray: t.border };
  const borderMap = { green: '#22c55e', yellow: '#f59e0b', red: '#ef4444', lightgray: t.border };

  return (
    <div style={{ background: t.bg, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px 0', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 40, objectFit: 'contain' }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.text1 }}>Réservation</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Bonjour, {fullName}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? 'translateX(16px)' : 'translateX(0)' }} />
            </button>
            <button onClick={() => navigate('/my-bookings')} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
              📋 {isMobile ? '' : 'Mes réservations'}
            </button>
            <button onClick={() => navigate('/')} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
              ← {isMobile ? '' : 'Retour'}
            </button>
          </div>
        </div>

        {/* Form card */}
        <div style={{ margin: '20px 18px 0', background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: isMobile ? '16px 14px' : '24px' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: t.text3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>Sélectionnez votre créneau</div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 140px', minWidth: 130 }}>
              <div style={{ fontSize: 12, color: t.text2, marginBottom: 6 }}>📅 Date</div>
              <DatePicker
                value={date} onChange={setDate} format="DD/MM/YYYY"
                placeholder="Sélectionner" size="large"
                style={{ width: '100%', borderRadius: 10 }}
                disabledDate={current => {
                  const today = dayjs().startOf('day');
                  return current && (current < today || current > today.add(1, 'day'));
                }}
              />
            </div>
            <div style={{ flex: '1 1 120px', minWidth: 110 }}>
              <div style={{ fontSize: 12, color: t.text2, marginBottom: 6 }}>🕐 Heure</div>
              <TimePicker
                value={time} onChange={setTime} format="HH:mm"
                minuteStep={15} size="large" placeholder="Heure"
                style={{ width: '100%', borderRadius: 10 }}
                disabledHours={() => {
                  const { startHour, endHour } = openingHours;
                  // Earliest selectable time = now + beforeMinutes
                  const earliestMs = Date.now() + beforeMinutes * 60000;
                  const earliest = dayjs(earliestMs);
                  const isToday = date?.isSame(dayjs(), 'day');
                  const minHour = isToday ? earliest.hour() : startHour;
                  return Array.from({ length: 24 }, (_, i) => i).filter(
                    h => h < Math.max(startHour, minHour) || h > endHour
                  );
                }}
                disabledMinutes={hour => {
                  const valid = [0, 15, 30, 45];
                  let disabled = Array.from({ length: 60 }, (_, i) => i).filter(m => !valid.includes(m));
                  const isToday = date?.isSame(dayjs(), 'day');
                  if (isToday) {
                    const earliest = dayjs(Date.now() + beforeMinutes * 60000);
                    if (hour === earliest.hour()) {
                      // disable minutes before earliest minute (rounded up to next 15)
                      disabled = disabled.concat(valid.filter(m => m < earliest.minute()));
                    }
                  }
                  return disabled;
                }}
              />
            </div>
            <div style={{ flex: '1 1 110px', minWidth: 100 }}>
              <div style={{ fontSize: 12, color: t.text2, marginBottom: 6 }}>⏱️ Durée</div>
              <Select value={duration} onChange={setDuration} size="large" style={{ width: '100%', borderRadius: 10 }}>
                {options.map(h => <Option key={h} value={h}>{h}h</Option>)}
              </Select>
            </div>
            <div style={{ flex: '1 1 160px', minWidth: 150 }}>
              <button
                onClick={loadSlots}
                disabled={checkingAvailability}
                style={{ ...s.primaryBtn, background: t.blue, width: '100%', height: 40, opacity: checkingAvailability ? 0.7 : 1 }}
              >
                {checkingAvailability ? 'Vérification...' : 'Vérifier la disponibilité'}
              </button>
            </div>
          </div>

          {cancelledUntil && cancelledUntil > new Date() && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#fef2f2', borderRadius: 10, fontSize: 12, color: '#dc2626', border: '1px solid #fecaca' }}>
              🚫 Suite à une annulation, vous pouvez effectuer une nouvelle réservation à partir de{' '}
              <strong>{dayjs(cancelledUntil).format('HH:mm')}</strong>.
            </div>
          )}

          {slots && !( cancelledUntil && cancelledUntil > new Date() ) && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: t.blueBg, borderRadius: 10, fontSize: 12, color: t.blueText }}>
              📌 {slots.start.format('DD/MM/YYYY')} · {slots.start.format('HH:mm')} → {slots.end.format('HH:mm')}
            </div>
          )}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 10, padding: '16px 18px 0', flexWrap: 'wrap' }}>
          {[
            { color: colorMap.green, label: 'Disponible', count: availableCount },
            { color: colorMap.yellow, label: 'Réservé', count: reservedCount },
            { color: colorMap.red, label: 'Occupé', count: occupiedCount },
          ].map(({ color, label, count }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 10, padding: '8px 12px', flex: '1 1 100px' }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: t.text2 }}>{label}</span>
              {slots && <span style={{ fontSize: 12, fontWeight: 600, color: t.text1, marginLeft: 'auto' }}>{count}</span>}
            </div>
          ))}
        </div>

        {/* Floor plan */}
        <div style={{ margin: '16px 18px 0' }}>
          {!slots ? (
            <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: '48px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🪑</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: t.text1, marginBottom: 6 }}>Prêt à réserver ?</div>
              <div style={{ fontSize: 12, color: t.text2 }}>Sélectionnez un créneau puis cliquez sur "Vérifier la disponibilité"</div>
            </div>
          ) : checkingAvailability ? (
            <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 48, textAlign: 'center' }}>
              <Spin size="large" />
              <div style={{ fontSize: 13, color: t.text2, marginTop: 14 }}>Chargement du plan...</div>
            </div>
          ) : (
            <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: t.text1 }}>Plan de la salle</div>
                {isMobile && <div style={{ fontSize: 11, color: t.text3 }}>Pincez pour zoomer · Faites défiler</div>}
              </div>

              {/* Pinch-to-zoom wrapper */}
              <div style={{
                overflowX: 'auto', overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                maxHeight: isMobile ? '65vh' : 'none',
                padding: 16,
              }}>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  touchAction: 'pinch-zoom',
                }}>
                  <img
                    ref={imgRef}
                    src="/PlanSvg.svg"
                    alt="Plan de la salle"
                    onLoad={updateImgSize}
                    style={{ width: '100%', height: 'auto', display: 'block', userSelect: 'none', pointerEvents: 'none' }}
                    draggable={false}
                  />
                  {imgSize.w > 0 && seatList.map(([id, x, y]) => {
                    const ratioX = imgSize.w / NATIVE_W;
                    const ratioY = imgSize.h / NATIVE_H;
                    const scaledX = x * ratioX;
                    const scaledY = y * ratioY;
                    const dotSize = Math.max(20, Math.round(28 * ratioX));
                    const fontSize = Math.max(8, Math.round(11 * ratioX));
                    const color = deskStatus[id] || 'lightgray';
                    const isAvailable = color === 'green';
                    return (
                      <Tooltip
                        key={id}
                        title={`Table ${id} · ${color === 'green' ? 'Disponible' : color === 'yellow' ? 'Réservée' : 'Occupée'}`}
                        placement="top"
                      >
                        <div
                          onClick={() => onDeskClick(id)}
                          style={{
                            position: 'absolute',
                            top: scaledY,
                            left: scaledX,
                            width: dotSize,
                            height: dotSize,
                            marginTop: -(dotSize / 2),
                            marginLeft: -(dotSize / 2),
                            backgroundColor: colorMap[color] || t.border,
                            borderRadius: '50%',
                            border: `2px solid ${borderMap[color] || t.border}`,
                            cursor: isAvailable ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize,
                            color: '#fff',
                            transform: selectedDeskId === id ? 'scale(1.2)' : 'scale(1)',
                            transition: 'transform 0.15s',
                            boxShadow: isAvailable ? `0 0 6px ${colorMap.green}88` : 'none',
                            zIndex: 2,
                          }}
                        >
                          {id}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 600 }}>✅ Confirmer la réservation</span>}
        open={isModalVisible}
        onCancel={() => { setIsModalVisible(false); setSelectedDeskId(null); }}
        footer={[
          <Button key="cancel" onClick={() => { setIsModalVisible(false); setSelectedDeskId(null); }} style={{ borderRadius: 8 }}>
            Annuler
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirm} loading={loading} style={{ borderRadius: 8, background: '#16a34a', border: 'none' }}>
            Confirmer
          </Button>,
        ]}
        width={isMobile ? '92vw' : 460}
        style={{ top: isMobile ? 40 : 100 }}
      >
        {selectedDeskId && slots ? (
          <div>
            <div style={{ background: '#f8fafc', borderRadius: 12, padding: 18, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 12 }}>Détails de la réservation</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: '#334155' }}>
                <div>🪑 Table <span style={{ fontWeight: 700, color: '#2563eb', fontSize: 16 }}>#{selectedDeskId}</span></div>
                <div>📅 {slots.start.format('DD/MM/YYYY')}</div>
                <div>🕐 {slots.start.format('HH:mm')} → {slots.end.format('HH:mm')}</div>
                <div>⏱️ {duration} heure{duration > 1 ? 's' : ''}</div>
              </div>
            </div>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#92400e' }}>
              ⚠️ Votre table est retenue 15 min · Une réservation par jour · Non transférable
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        )}
      </Modal>
    </div>
  );
}

const theme = {
  light: {
    bg: '#f8fafc', bg3: '#f1f5f9', border: '#e2e8f0',
    text1: '#0f172a', text2: '#64748b', text3: '#94a3b8',
    blue: '#2563eb', blueBg: '#eff6ff', blueText: '#1d4ed8',
    cardBg: '#ffffff',
  },
  dark: {
    bg: '#0f172a', bg3: '#1a2740', border: '#273549',
    text1: '#f1f5f9', text2: '#94a3b8', text3: '#475569',
    blue: '#378ADD', blueBg: '#1e3a5f', blueText: '#85B7EB',
    cardBg: '#1e293b',
  },
};

const s = {
  toggleTrack: { width: 36, height: 20, borderRadius: 20, cursor: 'pointer', position: 'relative', flexShrink: 0 },
  toggleKnob: { width: 14, height: 14, borderRadius: '50%', position: 'absolute', top: 2, left: 2, transition: 'transform 0.2s' },
  outlineBtn: { fontSize: 12, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' },
  primaryBtn: { fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', transition: 'opacity 0.15s' },
};